import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon,
  Star as StarIcon,
  MonetizationOn as MonetizationOnIcon
} from '@mui/icons-material';
import { adminService } from '../../services/api';

interface DistributionSummary {
  total_distributions: number;
  total_tfiats_distributed: number;
  distribution_by_type: {
    [key: string]: {
      count: number;
      total_amount: number;
    };
  };
}

const TfiatDistributionSummary: React.FC = () => {
  const [data, setData] = useState<DistributionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDistributionSummary();
  }, []);

  const fetchDistributionSummary = async () => {
    try {
      setLoading(true);
      const response = await adminService.getTfiatDistributionSummary();
      setData(response);
    } catch (error: any) {
      console.error('Error al obtener resumen de distribución:', error);
      setError(error.response?.data?.detail || 'Error al cargar resumen de distribución');
    } finally {
      setLoading(false);
    }
  };

  const getDistributionTypeLabel = (type: string): string => {
    const labels: { [key: string]: string } = {
      'estrella_padre_distribution': 'Estrella Padre',
      'estrella_abuelo_distribution': 'Estrella Abuelo',
      'estrella_bisabuelo_distribution': 'Estrella Bisabuelo',
      'host_distribution': 'Host del Usuario',
      'host_2do_nivel_distribution': 'Host de 2do Nivel',
      'company_distribution': 'Compañía'
    };
    return labels[type] || type;
  };

  const getDistributionTypeColor = (type: string): string => {
    const colors: { [key: string]: string } = {
      'estrella_padre_distribution': '#3B82F6', // Azul
      'estrella_abuelo_distribution': '#8B5CF6', // Morado
      'estrella_bisabuelo_distribution': '#EC4899', // Rosa
      'host_distribution': '#10B981', // Verde
      'host_2do_nivel_distribution': '#F59E0B', // Amarillo
      'company_distribution': '#EF4444' // Rojo
    };
    return colors[type] || '#6B7280';
  };

  const getDistributionTypeIcon = (type: string) => {
    if (type.includes('estrella')) return <StarIcon />;
    if (type.includes('host')) return <AccountBalanceIcon />;
    if (type.includes('company')) return <MonetizationOnIcon />;
    return <TrendingUpIcon />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info">
        No hay datos de distribución disponibles
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Typography variant="h5" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        📊 Resumen de Distribución de Tfiats
      </Typography>

      {/* Tarjetas de Resumen */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700}>
                {data.total_distributions}
              </Typography>
              <Typography variant="body2">
                Distribuciones Totales
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: 'success.main', color: 'white' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700}>
                {data.total_tfiats_distributed.toFixed(2)} TF
              </Typography>
              <Typography variant="body2">
                Total Tfiats Distribuidos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: 'info.main', color: 'white' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700}>
                {data.total_distributions > 0 ? (data.total_tfiats_distributed / data.total_distributions).toFixed(2) : '0.00'} TF
              </Typography>
              <Typography variant="body2">
                Promedio por Distribución
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Explicación de la Lógica de Distribución */}
      <Paper sx={{ p: 3, mb: 4, bgcolor: '#f8f9fa' }}>
        <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          🎯 Lógica de Distribución Automática
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Cada vez que se genera una estrella en el árbol binario, se distribuyen automáticamente 1 Tfiat completo de la siguiente manera:
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">⭐ Estrella Padre:</Typography>
              <Chip label="0.06 TF" size="small" color="primary" />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">⭐ Estrella Abuelo:</Typography>
              <Chip label="0.12 TF" size="small" color="primary" />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">⭐ Estrella Bisabuelo:</Typography>
              <Chip label="0.30 TF" size="small" color="primary" />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">🏠 Host del Usuario:</Typography>
              <Chip label="0.35 TF" size="small" color="success" />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">🏢 Host de 2do Nivel:</Typography>
              <Chip label="0.15 TF" size="small" color="warning" />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">🏭 Compañía:</Typography>
              <Chip label="0.02 TF" size="small" color="error" />
            </Box>
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.main', color: 'white', borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="body1" fontWeight={600}>
            🎯 Total: 1.00 TF por cada estrella generada
          </Typography>
        </Box>
      </Paper>

      {/* Tabla de Distribuciones por Tipo */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Typography variant="h6" fontWeight={600} sx={{ p: 3, pb: 2 }}>
          📋 Detalle de Distribuciones por Tipo
        </Typography>
        
        <TableContainer className="mobile-table-scroll">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tipo de Distribución</TableCell>
                <TableCell align="center">Cantidad por Estrella</TableCell>
                <TableCell align="center">Número de Distribuciones</TableCell>
                <TableCell align="right">Total Tfiats Distribuidos</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(data.distribution_by_type).map(([type, info]) => (
                <TableRow key={type} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ color: getDistributionTypeColor(type) }}>
                        {getDistributionTypeIcon(type)}
                      </Box>
                      <Typography variant="body2" fontWeight={600}>
                        {getDistributionTypeLabel(type)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={`${(info.total_amount / info.count).toFixed(2)} TF`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight={600}>
                      {info.count}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600} color="success.main">
                      {info.total_amount.toFixed(2)} TF
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default TfiatDistributionSummary;
